import { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { ArrowLeft, Trash2, Plus, X, Send, MessageSquare, Zap, List, ExternalLink, Image, LayoutGrid, ShoppingBag, GalleryHorizontal } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { WhatsAppPhonePreview } from "@/components/whatsapp/WhatsAppPhonePreview";
import { useWhatsAppMessageTemplates, WhatsAppMessageTemplate } from "@/hooks/useWhatsAppMessageTemplates";
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { formatDistanceToNow } from "date-fns";
import { ptBR } from "date-fns/locale";
import { CountryCodeSelect } from "@/components/common/CountryCodeSelect";

const statusConfig: Record<string, { className: string; label: string }> = {
  approved: { className: "bg-emerald-500/15 text-emerald-700 border-emerald-500/30", label: "Aprovado" },
  pending_approval: { className: "bg-amber-500/15 text-amber-700 border-amber-500/30", label: "Aguardando" },
  rejected: { className: "bg-red-500/15 text-red-700 border-red-500/30", label: "Rejeitado" },
  draft: { className: "bg-muted text-muted-foreground border-border", label: "Rascunho" },
};

const categoryLabels: Record<string, string> = {
  UTILITY: "Utilidade",
  MARKETING: "Marketing",
  AUTHENTICATION: "Autenticação",
  SERVICE: "Serviço",
};

interface ContentTypeOption {
  value: string;
  label: string;
  description: string;
  icon: LucideIcon;
}

const contentTypeOptions: ContentTypeOption[] = [
  { value: "TEXT", label: "Texto", description: "Mensagem simples de texto", icon: MessageSquare },
  { value: "QUICK_REPLY", label: "Resposta Rápida", description: "Botões de resposta rápida (máx 10)", icon: Zap },
  { value: "LIST_PICKER", label: "Lista", description: "Menu com lista de opções (máx 10)", icon: List },
  { value: "CALL_TO_ACTION", label: "Call-to-Action", description: "Botões com links ou telefone", icon: ExternalLink },
  { value: "MEDIA", label: "Mídia", description: "Imagem, vídeo ou documento", icon: Image },
  { value: "CARD", label: "Card", description: "Card com imagem e botões", icon: LayoutGrid },
  { value: "CATALOG", label: "Catálogo", description: "Catálogo de produtos", icon: ShoppingBag },
  { value: "CAROUSEL", label: "Carrossel", description: "Sequência de cards", icon: GalleryHorizontal },
];

const contentTypeLabels: Record<string, string> = Object.fromEntries(
  contentTypeOptions.map((o) => [o.value, o.label])
);

export default function WhatsAppTemplateDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id || id === "new";
  const { createTemplate, updateTemplate, deleteTemplate, submitToTwilio } = useWhatsAppMessageTemplates();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isSendingTest, setIsSendingTest] = useState(false);
  const [variables, setVariables] = useState<string[]>([]);
  const [exampleValues, setExampleValues] = useState<string[]>([]);
  const [newVariableModule, setNewVariableModule] = useState("");
  const [newVariableField, setNewVariableField] = useState("");
  const [testPhone, setTestPhone] = useState("");
  const [testCountryCode, setTestCountryCode] = useState("+55");
  const [buttons, setButtons] = useState<Array<{ text: string }>>([]);

  const { data: template, isLoading } = useQuery({
    queryKey: ['whatsapp-message-template', id],
    queryFn: async () => {
      if (isNew) return null;
      const { data, error } = await supabase
        .from('whatsapp_message_templates')
        .select('*')
        .eq('id', id)
        .single();
      if (error) throw error;
      return data as WhatsAppMessageTemplate;
    },
    enabled: !isNew,
  });

  const { register, handleSubmit, watch, setValue, reset, formState: { errors } } = useForm({
    defaultValues: {
      name: "",
      template_name: "",
      language: "pt_BR",
      category: "MARKETING",
      content_type: "TEXT",
      header_type: "",
      header_text: "",
      body_text: "",
      footer_text: "",
      provider: "twilio",
      status: "draft",
    },
  });

  useEffect(() => {
    if (template) {
      reset({
        name: template.name || "",
        template_name: template.template_name || "",
        language: template.language || "pt_BR",
        category: template.category || "MARKETING",
        content_type: template.content_type || "TEXT",
        header_type: template.header_type || "",
        header_text: template.header_text || "",
        body_text: template.body_text || "",
        footer_text: template.footer_text || "",
        provider: template.provider || "twilio",
        status: template.status || "draft",
      });
      setVariables(template.variables || []);
      setExampleValues(template.example_values || []);
      setButtons(template.buttons || []);
    }
  }, [template, reset]);

  const watchedValues = watch();

  const detectedVariables = useMemo(() => {
    const matches = watchedValues.body_text?.match(/\{\{(\d+)\}\}/g) || [];
    return [...new Set(matches)];
  }, [watchedValues.body_text]);

  const addVariable = () => {
    if (newVariableModule && newVariableField) {
      const variableName = `${newVariableModule}.${newVariableField}`;
      setVariables([...variables, variableName]);
      setNewVariableModule("");
      setNewVariableField("");
    }
  };

  const removeVariable = (index: number) => {
    setVariables(variables.filter((_, i) => i !== index));
    setExampleValues(exampleValues.filter((_, i) => i !== index));
  };

  const handleFormSubmit = (data: any) => {
    const payload = {
      ...data,
      variables,
      example_values: exampleValues,
      buttons: watchedValues.content_type === 'QUICK_REPLY' ? buttons : [],
      status: isNew ? 'draft' : data.status,
    };

    if (isNew) {
      createTemplate.mutate(payload, {
        onSuccess: () => navigate(-1),
      });
    } else {
      updateTemplate.mutate({ ...payload, id: id! }, {
        onSuccess: () => navigate(-1),
      });
    }
  };

  const handleDelete = () => {
    if (id) {
      deleteTemplate.mutate(id, {
        onSuccess: () => navigate(-1),
      });
    }
    setDeleteDialogOpen(false);
  };

  const handleTestSend = async () => {
    if (!testPhone) {
      toast.error("Informe o número de telefone");
      return;
    }
    if (!template?.template_id) {
      toast.error("Template sem ID do Twilio. Sincronize os templates primeiro.");
      return;
    }

    const fullPhone = `${testCountryCode}${testPhone.replace(/\D/g, '')}`;
    setIsSendingTest(true);

    try {
      const { data, error } = await supabase.functions.invoke('twilio-whatsapp', {
        body: {
          action: 'sendTemplate',
          to: fullPhone,
          template_sid: template.template_id,
          template_variables: exampleValues.filter(v => v),
        }
      });

      if (error) throw new Error(error.message);
      if (data?.success === false || data?.error) throw new Error(data.error || 'Erro desconhecido');

      toast.success('Mensagem de teste enviada com sucesso!');
    } catch (err: any) {
      console.error('Erro ao enviar teste:', err);
      toast.error(err?.message || 'Falha ao enviar mensagem de teste');
    } finally {
      setIsSendingTest(false);
    }
  };

  if (isLoading) {
    return (
      <div className="container mx-auto py-6">
        <div className="text-center py-12 text-muted-foreground">Carregando...</div>
      </div>
    );
  }

  const sc = statusConfig[watchedValues.status] || statusConfig.draft;

  return (
    <div className="container mx-auto py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold">
                {isNew ? "Novo Template" : (watchedValues.name || template?.name || "Template")}
              </h1>
              {!isNew && (
                <>
                  <Badge variant="outline" className={sc.className}>{sc.label}</Badge>
                  <Badge variant="outline">
                    {contentTypeLabels[watchedValues.content_type] || watchedValues.content_type}
                  </Badge>
                </>
              )}
            </div>
            {!isNew && template?.template_name && (
              <p className="text-sm text-muted-foreground mt-0.5">
                {template.template_name}
              </p>
            )}
          </div>
        </div>
        {!isNew && (
          <Button variant="outline" className="text-destructive hover:text-destructive" onClick={() => setDeleteDialogOpen(true)}>
            <Trash2 className="mr-2 h-4 w-4" />
            Excluir
          </Button>
        )}
      </div>

      <form onSubmit={handleSubmit(handleFormSubmit)}>
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* Left Column - Form */}
          <div className="lg:col-span-3 space-y-6">
            {/* Template Info Card */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Informações do Template</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="name">Nome do Template *</Label>
                    <Input
                      id="name"
                      {...register("name", { required: true })}
                      placeholder="Ex: Lembrete de Pagamento"
                    />
                    {errors.name && <span className="text-sm text-destructive">Obrigatório</span>}
                  </div>
                  <div>
                    <Label htmlFor="template_name">Nome Técnico *</Label>
                    <Input
                      id="template_name"
                      {...register("template_name", { required: true })}
                      placeholder="lembrete_pagamento"
                      className="lowercase"
                    />
                    {errors.template_name && <span className="text-sm text-destructive">Obrigatório</span>}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Idioma</Label>
                    <Select value={watchedValues.language} onValueChange={(v) => setValue("language", v)}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pt_BR">Português (BR)</SelectItem>
                        <SelectItem value="en_US">Inglês (US)</SelectItem>
                        <SelectItem value="es_ES">Espanhol</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Categoria</Label>
                    <Select value={watchedValues.category} onValueChange={(v) => setValue("category", v)}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="MARKETING">Marketing</SelectItem>
                        <SelectItem value="UTILITY">Utilidade</SelectItem>
                        <SelectItem value="AUTHENTICATION">Autenticação</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label>Tipo de Template</Label>
                  <Select value={watchedValues.content_type} onValueChange={(v) => setValue("content_type", v)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {contentTypeOptions.map((option) => {
                        const Icon = option.icon;
                        return (
                          <SelectItem key={option.value} value={option.value} className="py-2.5">
                            <div className="flex items-center gap-3">
                              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-emerald-500/10 shrink-0">
                                <Icon className="h-4 w-4 text-emerald-600" />
                              </div>
                              <div className="flex flex-col">
                                <span className="font-medium text-sm">{option.label}</span>
                                <span className="text-xs text-muted-foreground">{option.description}</span>
                              </div>
                            </div>
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Provider</Label>
                  <Select value={watchedValues.provider} onValueChange={(v) => setValue("provider", v)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="twilio">Twilio</SelectItem>
                      <SelectItem value="evolution">Evolution API</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Tipo de Header</Label>
                  <Select
                    value={watchedValues.header_type || "none"}
                    onValueChange={(v) => setValue("header_type", v === "none" ? "" : v)}
                  >
                    <SelectTrigger><SelectValue placeholder="Nenhum" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Nenhum</SelectItem>
                      <SelectItem value="TEXT">Texto</SelectItem>
                      <SelectItem value="IMAGE">Imagem</SelectItem>
                      <SelectItem value="VIDEO">Vídeo</SelectItem>
                      <SelectItem value="DOCUMENT">Documento</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {watchedValues.header_type === "TEXT" && (
                  <div>
                    <Label>Texto do Header</Label>
                    <Input {...register("header_text")} placeholder="Título da mensagem" />
                  </div>
                )}

                <div>
                  <Label>Corpo da Mensagem *</Label>
                  <Textarea
                    {...register("body_text", { required: true })}
                    placeholder="Use {{1}}, {{2}}, etc para variáveis"
                    rows={5}
                    className="bg-muted/50"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Use {`{{1}}`}, {`{{2}}`}, etc para variáveis dinâmicas
                  </p>
                  {errors.body_text && <span className="text-sm text-destructive">Obrigatório</span>}
                </div>

                <div>
                  <Label>Rodapé (Opcional)</Label>
                  <Input {...register("footer_text")} placeholder="Texto pequeno no rodapé" />
                </div>

                {/* Quick Reply Buttons */}
                {watchedValues.content_type === "QUICK_REPLY" && (
                  <div>
                    <Label>Botões de Resposta Rápida</Label>
                    <div className="space-y-2 mt-2">
                      {buttons.map((button, index) => (
                        <div key={index} className="flex items-center gap-2">
                          <Input
                            placeholder="Texto do botão"
                            value={button.text}
                            maxLength={25}
                            onChange={(e) => {
                              const updated = [...buttons];
                              updated[index] = { text: e.target.value };
                              setButtons(updated);
                            }}
                          />
                          <span className="text-xs text-muted-foreground shrink-0">{button.text.length}/25</span>
                          <Button type="button" variant="ghost" size="sm" onClick={() => setButtons(buttons.filter((_, i) => i !== index))}>
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                      <div className="flex items-center justify-between">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          disabled={buttons.length >= 10}
                          onClick={() => setButtons([...buttons, { text: "" }])}
                        >
                          <Plus className="h-4 w-4 mr-1" />
                          Adicionar Botão
                        </Button>
                        <span className="text-xs text-muted-foreground">{buttons.length}/10 botões</span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Variables */}
                <div>
                  <Label>Variáveis</Label>
                  <div className="space-y-2 mt-2">
                    {variables.map((variable, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <span className="text-sm font-mono bg-muted px-2 py-1 rounded shrink-0">
                          {`{{${index + 1}}}`}
                        </span>
                        <span className="text-sm flex-1 truncate">{variable}</span>
                        <Input
                          placeholder="Valor de exemplo"
                          value={exampleValues[index] || ""}
                          onChange={(e) => {
                            const newExamples = [...exampleValues];
                            newExamples[index] = e.target.value;
                            setExampleValues(newExamples);
                          }}
                          className="flex-1"
                        />
                        <Button type="button" variant="ghost" size="sm" onClick={() => removeVariable(index)}>
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                    <div className="flex gap-2">
                      <Select value={newVariableModule} onValueChange={setNewVariableModule}>
                        <SelectTrigger className="flex-1"><SelectValue placeholder="Módulo" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="lead">Lead</SelectItem>
                          <SelectItem value="contact">Contato</SelectItem>
                        </SelectContent>
                      </Select>
                      <Select value={newVariableField} onValueChange={setNewVariableField} disabled={!newVariableModule}>
                        <SelectTrigger className="flex-1"><SelectValue placeholder="Campo" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="first_name">Nome</SelectItem>
                          <SelectItem value="last_name">Sobrenome</SelectItem>
                          <SelectItem value="phone">Telefone</SelectItem>
                          <SelectItem value="email">Email</SelectItem>
                        </SelectContent>
                      </Select>
                      <Button type="button" variant="outline" size="icon" onClick={addVariable}>
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Footer info */}
                {!isNew && template && (
                  <div className="pt-4 border-t border-border flex gap-6 text-xs text-muted-foreground">
                    <span>Criado {formatDistanceToNow(new Date(template.created_at), { addSuffix: true, locale: ptBR })}</span>
                    <span>Atualizado {formatDistanceToNow(new Date(template.updated_at), { addSuffix: true, locale: ptBR })}</span>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Test Send Card */}
            {!isNew && template?.status === 'approved' && (<Card>
              <CardHeader>
                <CardTitle className="text-lg">Testar Envio</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Número de Telefone</Label>
                  <div className="flex gap-2">
                    <CountryCodeSelect value={testCountryCode} onChange={setTestCountryCode} className="w-[120px]" />
                    <Input
                      placeholder="11999999999"
                      value={testPhone}
                      onChange={(e) => setTestPhone(e.target.value)}
                    />
                  </div>
                </div>

                {detectedVariables.length > 0 && (
                  <div className="space-y-2">
                    <Label>Valores das Variáveis</Label>
                    {detectedVariables.map((v, i) => (
                      <div key={v} className="flex items-center gap-2">
                        <span className="text-sm font-mono bg-muted px-2 py-1 rounded shrink-0">{v}</span>
                        <Input
                          placeholder={`Valor para ${v}`}
                          value={exampleValues[i] || ""}
                          onChange={(e) => {
                            const newExamples = [...exampleValues];
                            newExamples[i] = e.target.value;
                            setExampleValues(newExamples);
                          }}
                        />
                      </div>
                    ))}
                  </div>
                )}

                <Button type="button" onClick={handleTestSend} className="w-full" disabled={isSendingTest}>
                  {isSendingTest ? (
                    <span className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                  ) : (
                    <Send className="mr-2 h-4 w-4" />
                  )}
                  {isSendingTest ? "Enviando..." : "Enviar Teste"}
                </Button>
              </CardContent>
            </Card>)}

            {/* Action Buttons */}
            <div className="flex justify-end gap-3">
              <Button type="button" variant="outline" onClick={() => navigate(-1)}>
                Cancelar
              </Button>
              {!isNew && template?.status === 'draft' && (
                <Button
                  type="button"
                  variant="secondary"
                  disabled={submitToTwilio.isPending}
                  onClick={() => {
                    const bodyVars = watchedValues.body_text?.match(/\{\{(\d+)\}\}/g) || [];
                    if (bodyVars.length > 0) {
                      const missingExamples = bodyVars.some((_: string, i: number) => !exampleValues[i]?.trim());
                      if (missingExamples) {
                        toast.error('Preencha os valores de exemplo para todas as variáveis antes de enviar para aprovação.');
                        return;
                      }
                    }
                    handleSubmit(async (data) => {
                      const payload = {
                        ...data,
                        variables,
                        example_values: exampleValues,
                        buttons: watchedValues.content_type === 'QUICK_REPLY' ? buttons : [],
                        status: data.status,
                      };
                      await updateTemplate.mutateAsync({ ...payload, id: id! });
                      submitToTwilio.mutate(id!);
                    })();
                  }}
                >
                  {submitToTwilio.isPending ? (
                    <span className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                  ) : (
                    <Send className="mr-2 h-4 w-4" />
                  )}
                  Enviar para Aprovação
                </Button>
              )}
              <Button type="submit">
                {isNew ? "Criar" : "Salvar"} Template
              </Button>
            </div>
          </div>

          {/* Right Column - Preview */}
          <div className="lg:col-span-2">
            <div className="sticky top-6">
              <Label className="mb-3 block">Preview</Label>
              <WhatsAppPhonePreview
                headerType={watchedValues.header_type}
                headerText={watchedValues.header_text}
                bodyText={watchedValues.body_text || "Sua mensagem aparecerá aqui..."}
                footerText={watchedValues.footer_text}
                buttons={watchedValues.content_type === 'QUICK_REPLY' ? buttons : undefined}
                exampleValues={exampleValues}
              />
            </div>
          </div>
        </div>
      </form>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja deletar este template? Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete}>Deletar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
